import React, { useEffect, useRef } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  createStyles,
  Group,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconDeviceDesktop, IconLock, IconPlus, IconX } from '@tabler/icons-react';
import ExpandableSection from 'components/ExpandableSection';
import WorkoutRichTextEditor from 'components/workout-program/WorkoutRichTextEditor';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import { useScrollContainer } from 'hooks/useScrollContainer';
import useAssignWorkout from 'hooks/workout/useAssignWorkout';
import useCreateWorkoutTemplate from 'hooks/workoutTemplate/useCreateWorkoutTemplate';
import { buildTemplatePayloadFromFormValues } from 'pages/private/owner/Workouts/Programming/components/AssignWorkoutModal/utils/templateToWorkoutWrite';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScoreTypeEnum } from 'types/enums';
import { getAlphabetLetterByIndex } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { assignmentTargetFields, type AssignmentTarget } from 'utils/workout.helpers';

import { createEmptySection, duplicateSection } from '../../../forms/workoutBuilder/createSection';
import {
  collectSectionErrors,
  extractMovementScoringPayload,
  hasMovementScoring,
} from '../../../forms/workoutBuilder/scoringFormHelpers';
import { WorkoutFormValues, WorkoutSectionData } from '../../../forms/workoutBuilder/types';
import { notifyWorkoutValidationErrors } from '../../../forms/workoutBuilder/workoutValidationToast';
import ModalWorkoutTemplatePreview, {
  WorkoutTemplatePreviewData,
} from '../../../Library/components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview';
import WorkoutSection from '../../../Library/pages/CreateWorkoutTemplate/components/WorkoutSection';
import useAssignTracking from '../../components/AssignWorkoutModal/useAssignTracking';
import { programmingReturnState } from '../../components/programmingReturnState';

interface CreateWorkoutLocationState {
  date: string;
  target: AssignmentTarget;
  prefillData?: Partial<WorkoutFormValues>;
  seededFromTemplate?: boolean;
  view?: 'week' | 'agenda';
  replacesWorkoutId?: string;
}

const useStyles = createStyles(theme => ({
  page: {
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.colors.gray[0],
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '1.5rem',
    paddingBottom: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    borderBottom: `0.0625rem solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.white,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerLeft: {
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexShrink: 0,
  },
  previewLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    '&:hover': {
      color: theme.colors.gray[9],
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  card: {
    marginBottom: '1rem',
    backgroundColor: theme.white,
    overflow: 'visible',
  },
  addSectionBar: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.colors.gray[0],
    paddingTop: '1rem',
    paddingBottom: '1.5rem',
    zIndex: 10,
  },
  addSectionButton: {
    width: '100%',
  },
}));

const CreateWorkout = (): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as CreateWorkoutLocationState | null;
  const returnView = state?.view ?? 'agenda';

  const [athleteNotesOpened, { toggle: toggleAthleteNotes }] = useDisclosure(false);
  const [coachNotesOpened, { toggle: toggleCoachNotes }] = useDisclosure(false);
  const [previewModalOpened, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);
  const prefillAppliedRef = useRef(false);
  const { containerRef, scrollToBottom } = useScrollContainer<HTMLDivElement>();

  const createMutation = useAssignWorkout(state?.replacesWorkoutId);
  const { trackConfirmed, trackCancelled } = useAssignTracking(state?.target);
  const saveAsTemplateMutation = useCreateWorkoutTemplate();
  const seededFromTemplate = !!state?.seededFromTemplate;
  const { successNotification, errorNotification } = notificationHandler();

  const formattedDate = state?.date ? dayjs(state.date).format('MMM D, YYYY') : '';
  const assignButtonDate = state?.date ? dayjs(state.date).format('MMM D') : '';
  const saveButtonLabel = state ? `Assign to ${state.target.name} on ${assignButtonDate}` : 'Save Workout';
  const initialValues: WorkoutFormValues = {
    name: '',
    athleteNotes: '',
    coachNotes: '',
    isVisibleToAthletes: true,
    sections: [createEmptySection({ id: 'section-a', letter: 'A' })],
  };

  const form = useForm<WorkoutFormValues>({
    initialValues,
    validate: {
      name: value => (value.trim().length === 0 ? 'Workout name is required' : null),
    },
  });

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (!state?.prefillData) return;
    form.setValues(state.prefillData);
    if (seededFromTemplate) {
      form.resetDirty({ ...initialValues, ...state.prefillData });
    }
    prefillAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSectionLetters = (sections: WorkoutSectionData[]): WorkoutSectionData[] => {
    return sections.map((section, index) => ({
      ...section,
      letter: getAlphabetLetterByIndex(index),
    }));
  };

  const handleAddSection = (): void => {
    const currentSections = form.values.sections;
    const nextLetter = getAlphabetLetterByIndex(currentSections.length);
    const newSection = createEmptySection({ letter: nextLetter });
    form.setFieldValue('sections', updateSectionLetters([...currentSections, newSection]));
    scrollToBottom();
  };

  const handleDuplicateSection = (index: number): void => {
    const currentSections = form.values.sections;
    const sectionToDuplicate = currentSections[index];
    const duplicatedSection = duplicateSection(sectionToDuplicate);
    const newSections = [
      ...currentSections.slice(0, index + 1),
      duplicatedSection,
      ...currentSections.slice(index + 1),
    ];

    form.setFieldValue('sections', updateSectionLetters(newSections));
    successNotification('Section duplicated', 'The section has been duplicated.');
  };

  const handleDeleteSection = (index: number): void => {
    if (form.values.sections.length > 1) {
      const newSections = form.values.sections.filter((_, i) => i !== index);
      form.setFieldValue('sections', updateSectionLetters(newSections));
    }
  };

  const validateWorkout = (actionLabel: string): boolean => {
    const { errors: nameErrors } = form.validate();
    const sectionErrors = collectSectionErrors(form.values.sections, form.setFieldError);
    const allErrors = { ...nameErrors, ...sectionErrors };
    if (Object.keys(allErrors).length === 0) return true;
    notifyWorkoutValidationErrors(errorNotification, allErrors, form.values.sections, actionLabel);
    return false;
  };

  const handleSave = (): void => {
    if (!validateWorkout('assign')) return;

    if (!state?.target) {
      errorNotification('Save failed', 'There is no class type or athlete to assign this workout to.');
      return;
    }

    const values = form.values;
    const payload = {
      name: values.name.trim(),
      athleteNotes: values.athleteNotes?.trim() || null,
      coachNotes: values.coachNotes?.trim() || null,
      isVisibleToAthletes: values.isVisibleToAthletes,
      date: state.date,
      ...assignmentTargetFields(state.target),
      workoutTemplate: null,
      sections: values.sections.map((section, sectionIndex) => ({
        name: section.name.trim(),
        workoutInstructions: section.description.trim(),
        orderIndex: sectionIndex,
        levelScaled: section.levelScaled,
        levelRx: section.levelRx,
        levelRxPlus: section.levelRxPlus,
        scoreEntireSection: section.scoreEntireSection,
        movements: (section.scoreEntireSection
          ? section.movements.filter(m => m.scoreType !== null)
          : section.movements.filter(hasMovementScoring)
        ).map((m, movementIndex) => ({
          workoutMovement: section.scoreEntireSection ? m.movementId || null : m.movementId,
          saveAsBenchmark: section.saveAsBenchmark,
          orderIndex: movementIndex,
          ...extractMovementScoringPayload(m),
        })),
      })),
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        trackConfirmed(payload);
        successNotification('Workout saved', 'Your workout has been saved and assigned.');
        navigate('/workouts/programming', {
          state: programmingReturnState(state.target, state.date, returnView),
        });
      },
      onError: error => {
        const errorMessage = error.response?.data?.message ?? 'Failed to save workout';
        errorNotification('Save failed', errorMessage);
      },
    });
  };

  const handleSaveAsTemplate = (): void => {
    if (!validateWorkout('save as template')) return;
    const payload = buildTemplatePayloadFromFormValues(form.values);
    saveAsTemplateMutation.mutate(payload, {
      onSuccess: () => successNotification('Template saved', `"${form.values.name}" saved as a template.`),
      onError: error => {
        const msg = error.response?.data?.message ?? 'Failed to save template';
        errorNotification('Save failed', msg);
      },
    });
  };

  const { hasPerm } = useAuth();
  const canMutate = hasPerm('WorkoutMutate');
  const saveAsTemplateDisabled = !canMutate || (seededFromTemplate && !form.isDirty());

  const handleClose = (): void => {
    trackCancelled();
    navigate('/workouts/programming', {
      state: programmingReturnState(state?.target, state?.date, returnView),
    });
  };

  const convertFormToPreviewData = (): WorkoutTemplatePreviewData => {
    const values = form.values;
    return {
      workoutTemplateId: undefined,
      name: values.name || 'Untitled Workout',
      athleteNotes: values.athleteNotes || null,
      coachNotes: values.coachNotes || null,
      sections: values.sections
        .filter(section => section.name || section.description)
        .map(section => {
          const synthMovementName = section.scoreEntireSection ? section.name.trim() || null : null;
          return {
            name: section.name || `Section ${section.letter}`,
            workoutInstructions: section.description || '',
            movements: section.movements
              .filter(m => m.scoreType !== null && (synthMovementName || (m.movementId && m.movementName)))
              .map(m => ({
                workoutMovement: {
                  workoutMovementId: m.movementId,
                  name: synthMovementName ?? m.movementName ?? 'Unknown Movement',
                },
                scoreType: m.scoreType as ScoreTypeEnum,
              })),
          };
        }),
    };
  };

  return (
    <>
      <ModalWorkoutTemplatePreview
        opened={previewModalOpened}
        template={convertFormToPreviewData()}
        handleClose={closePreviewModal}
        onEdit={closePreviewModal}
        showEditButton={false}
      />
      <Box className={classes.page} ref={containerRef}>
        <Box className={classes.content}>
          <header className={classes.header}>
            <div className={classes.headerLeft}>
              <Group spacing='sm' align='center' mb='xs'>
                <Title order={1}>Create Workout</Title>
                {state && (
                  <Badge color='blue' variant='light' size='lg'>
                    {state.target.name} · {formattedDate}
                  </Badge>
                )}
              </Group>
              <Text color='dimmed' size='sm'>
                Design and build your workout program.
              </Text>
            </div>
            <div className={classes.headerActions}>
              <Switch
                label={state?.target.kind === 'athlete' ? 'Visible to athlete' : 'Visible to athletes'}
                checked={form.values.isVisibleToAthletes}
                onChange={e => form.setFieldValue('isVisibleToAthletes', e.currentTarget.checked)}
              />
              <UnstyledButton
                className={classes.previewLink}
                onClick={openPreviewModal}
                data-cy='previewButton'
                aria-label='Preview'
              >
                <IconDeviceDesktop size={18} />
                <Text component='span'>Preview</Text>
              </UnstyledButton>
              <Button
                variant='default'
                onClick={handleSaveAsTemplate}
                loading={saveAsTemplateMutation.isLoading}
                disabled={saveAsTemplateDisabled || saveAsTemplateMutation.isLoading}
              >
                {saveAsTemplateMutation.isLoading ? 'Saving…' : 'Save as Template'}
              </Button>
              <Button
                onClick={handleSave}
                loading={createMutation.isLoading}
                disabled={!canMutate || createMutation.isLoading}
                data-cy='saveWorkoutButton'
              >
                {createMutation.isLoading ? (state ? 'Assigning…' : 'Saving…') : saveButtonLabel}
              </Button>
              <ActionIcon
                onClick={handleClose}
                size='lg'
                variant='subtle'
                color='gray'
                data-cy='closeButton'
                aria-label='Close'
              >
                <IconX size={20} />
              </ActionIcon>
            </div>
          </header>

          <Stack spacing='md'>
            <Card className={classes.card} p='md' radius='md' withBorder shadow='sm'>
              <Stack spacing='md'>
                <TextInput
                  label='Workout Name'
                  placeholder='Enter workout name'
                  required
                  {...form.getInputProps('name')}
                  data-cy='workoutNameInput'
                />
                <ExpandableSection
                  title={
                    <Text size='sm' weight={600}>
                      Athlete Notes
                    </Text>
                  }
                  opened={athleteNotesOpened}
                  handleToggle={toggleAthleteNotes}
                  dataCy='athleteNotesSection'
                >
                  <WorkoutRichTextEditor
                    value={form.values.athleteNotes ?? ''}
                    onChange={html => form.setFieldValue('athleteNotes', html)}
                    error={form.getInputProps('athleteNotes').error as string | undefined}
                    dataCy='athleteNotesTextarea'
                  />
                </ExpandableSection>
                <ExpandableSection
                  title={
                    <Group spacing='xs'>
                      <Text size='sm' weight={600}>
                        Coach Notes
                      </Text>
                      <Group spacing={4}>
                        <IconLock size={14} color='gray' />
                        <Text size='sm' color='dimmed'>
                          (Private)
                        </Text>
                      </Group>
                    </Group>
                  }
                  opened={coachNotesOpened}
                  handleToggle={toggleCoachNotes}
                  dataCy='coachNotesSection'
                >
                  <Textarea
                    placeholder='Internal notes for coaches only...'
                    {...form.getInputProps('coachNotes')}
                    minRows={3}
                    data-cy='coachNotesTextarea'
                  />
                </ExpandableSection>
              </Stack>
            </Card>

            {form.values.sections.map((section, index) => (
              <WorkoutSection
                key={section.id}
                section={section}
                index={index}
                form={form}
                onDelete={handleDeleteSection}
                onDuplicate={handleDuplicateSection}
                canDelete={form.values.sections.length > 1}
              />
            ))}
          </Stack>

          <Box className={classes.addSectionBar}>
            <Button
              className={classes.addSectionButton}
              leftIcon={<IconPlus size={16} />}
              onClick={handleAddSection}
              data-cy='addSectionButton'
            >
              Add Section
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CreateWorkout;
